import React, { useState } from 'react';
import { getDexterModelsByMachineType } from 'api/admin/devices/devices';
import toaster from 'react-hot-toast';

// components
import MachineTypeSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineTypeSelection';
import MachineModelSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineModelSelection';
import MachineNameSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineNameSelection';
import InitializationFormReview from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/InitializationFormReview';
import CentsLoader from 'components/common/centsLoader/CentsLoader';

const DexterDeviceInitializationSimulator = (props) => {
  const { device, manufacturer, onBack, onSubmit } = props;

  const dexterInitializationScreens = {
    MACHINE_TYPE: 'MACHINE_TYPE',
    MACHINE_MODEL: 'MACHINE_MODEL',
    MACHINE_NAME: 'MACHINE_NAME',
    UPPER_MACHINE_NAME: 'UPPER_MACHINE_NAME',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };
  const [initializationScreen, setInitializationScreen] = useState(
    dexterInitializationScreens.MACHINE_TYPE,
  );
  const [loading, setLoading] = useState(false);
  const [machineModels, setMachineModels] = useState([]);

  // form data
  const [selectedMachineType, setSelectedMachineType] = useState(null);
  const [selectedMachineName, setSelectedMachineName] = useState(null);
  const [selectedMachineModel, setSelectedMachineModel] = useState(null);
  const [selectedMachineModelDisplay, setSelectedMachineModelDisplay] = useState(null);
  const [selectedUpperMachineName, setSelectedUpperMachineName] = useState(null);
  
  /**
   * on the final review screen, if a stacked dryer is selected, navigating backwards
   * should take you to the upper machine name screen. otherwise, navigating
   * backwards should take you to the machine name screen
   */
  const handleGoBackForFinalReview = () => {
    if (selectedMachineModel.includes('X')) {
      setInitializationScreen(dexterInitializationScreens.UPPER_MACHINE_NAME);
    } else {
      setInitializationScreen(dexterInitializationScreens.MACHINE_NAME);
    }
  }
  
  /**
   * Depending on the current wizard screen, set the proper screen to show
   */
  const handleWizardGoBackFunctionality = () => {
    switch (initializationScreen) {
      case dexterInitializationScreens.MACHINE_TYPE:
        return onBack();
      
      case dexterInitializationScreens.MACHINE_MODEL:
        setSelectedMachineModel(null);
        setSelectedMachineModelDisplay(null);
        return setInitializationScreen(dexterInitializationScreens.MACHINE_TYPE);

      case dexterInitializationScreens.MACHINE_NAME:
        return setInitializationScreen(dexterInitializationScreens.MACHINE_MODEL);

      case dexterInitializationScreens.UPPER_MACHINE_NAME:
        return setInitializationScreen(dexterInitializationScreens.MACHINE_NAME);

      case dexterInitializationScreens.FINAL_REVIEW:
        return handleGoBackForFinalReview();

      default:
        return onBack();
    }
  }
  
  /**
   * disable the submit functionality if any of the following is missing or null:
   *
   * 1) manufacturer
   * 2) machine style
   * 3) machine type
   * 4) machine name
   */
  const disableFinalSubmitButton = () => {
    return !manufacturer || !selectedMachineModel || !selectedMachineType || !selectedMachineName;
  };

  /**
   * compile the form data and send back to DeviceSimulator
   */
  const submitDeviceInitializationFormData = () => {
    const finalData = {
      deviceId: device.id,
      manufacturer,
      machineModel: selectedMachineModel,
      machineType: selectedMachineType,
      lowerMachineName: selectedMachineName,
      upperMachineName: selectedUpperMachineName,
    };

    return onSubmit(finalData);
  };

  /**
   * render a summary screen for the user to review before submitting
   */
  const renderFinalDataReview = () => {
    return (
      <InitializationFormReview
        manufacturer={manufacturer}
        selectedMachineName={selectedMachineName}
        selectedMachineType={selectedMachineType}
        onBack={handleWizardGoBackFunctionality}
        onNext={submitDeviceInitializationFormData}
        disableFinalSubmitButton={disableFinalSubmitButton}
        selectedUpperMachineName={selectedUpperMachineName}
        selectedMachineModel={selectedMachineModel}
      />
    );
  };

  /**
   * move the wizard forward to the final review screen
   */
  const handleNexOnUpperMachineNameSelection = () => {
    setInitializationScreen(dexterInitializationScreens.FINAL_REVIEW);
  }

  /**
   * render the UI that allows users to provide the upper name of their new machine if they are pairing a stacked dryer
   */
  const renderUpperMachineNameSelection = () => {
    return (
      <MachineNameSelection
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNexOnUpperMachineNameSelection}
        selectedMachineName={selectedUpperMachineName}
        setSelectedMachineName={setSelectedUpperMachineName}
        title={"Choose the upper machine's name for your stacked dryer"}
      />
    );
  };

  /**
   * navigate to the final review screen when moving forward after machine name selection
   */
  const handleNextOnMachineNameSelection = () => {
    if (selectedMachineModel.includes('X')) {
      setInitializationScreen(dexterInitializationScreens.UPPER_MACHINE_NAME);
    } else {
      setInitializationScreen(dexterInitializationScreens.FINAL_REVIEW);
    }
  };

  /**
   * render the UI that allows users to provide the name of their new machine
   */
  const renderMachineNameSelection = () => {
    return (
      <MachineNameSelection
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineNameSelection}
        selectedMachineName={selectedMachineName}
        setSelectedMachineName={setSelectedMachineName}
        title={"Choose your machine's name"}
      />
    );
  };

  /**
   * set the display value and raw value for machine model selection in state
   * 
   * @param {object} machineModelData 
   */
  const handleMachineModelSelection = (machineModelData) => {
    setSelectedMachineModel(machineModelData.rawValue);
    setSelectedMachineModelDisplay(machineModelData.displayValue);
  }
  
  /**
   * move the wizard forward to machine name selection when a machine model is selected
   */
  const handleNextOnMachineModelSelection = () => {
    setInitializationScreen(dexterInitializationScreens.MACHINE_NAME);
  }
  
  /**
   * render the various machine models that can be selected for dexter machines
   */
  const renderMachineModelSelection = () => {
    return (
      <MachineModelSelection
        machineModels={machineModels}
        selectedMachineModelDisplay={selectedMachineModelDisplay}
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineModelSelection}
        onValueChange={handleMachineModelSelection}
        selectedMachineModel={selectedMachineModel}
      />
    );
  }
  
  /**
   * move the wizard forward from machine type selection to machine model selection
   */
  const handleNextOnMachineTypeSelection = async () => {
    try {
      setLoading(true);

      const { data } = await getDexterModelsByMachineType(selectedMachineType);
      setMachineModels(data.models);
      setInitializationScreen(dexterInitializationScreens.MACHINE_MODEL);
    } catch (error) {
      toaster(`Error fetching machine models: ${error?.response?.data?.error}`);
    } finally {
      setLoading(false);
    }
  }
  
  /**
   * render the dropdown UI to select machine type
   */
  const renderMachineTypeSelection = () => {
    return (
      <MachineTypeSelection
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineTypeSelection}
        onValueChange={(value) => { setSelectedMachineType(value)}}
        selectedMachineType={selectedMachineType}
      />
    );
  }

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderInitializationWizardOptions = () => {
    switch (initializationScreen) {
      case dexterInitializationScreens.MACHINE_TYPE:
        return renderMachineTypeSelection();
      case dexterInitializationScreens.MACHINE_MODEL:
        return renderMachineModelSelection();
      case dexterInitializationScreens.MACHINE_NAME:
        return renderMachineNameSelection();
      case dexterInitializationScreens.UPPER_MACHINE_NAME:
        return renderUpperMachineNameSelection();
      case dexterInitializationScreens.FINAL_REVIEW:
        return renderFinalDataReview();
      
      default:
        break;
    }
  };

  return loading ? <CentsLoader /> : renderInitializationWizardOptions();
};

export default DexterDeviceInitializationSimulator;
